import React, { useEffect, useState } from 'react';
import { X, Trophy, Send, TrendingUp } from 'lucide-react';
import api from '@/lib/api';

function Metric({ label, value, lead }) {
  return (
    <div className="flex-1">
      <div className={`font-display font-bold text-lg ${lead ? 'text-boosta-emerald' : 'text-white'}`}>{value ?? '—'}</div>
      <div className="text-[9px] uppercase tracking-wider text-white/40">{label}</div>
    </div>
  );
}

function Pane({ creative, label, perf, lead, onPick }) {
  const noData = perf && !perf.has_data;
  return (
    <div className="flex-1 flex flex-col" data-testid={`compare-pane-${label}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan">Variant {label}</span>
        {lead && <span className="text-[9px] px-2 py-0.5 rounded-full bg-boosta-emerald/15 text-boosta-emerald border border-boosta-emerald/30 uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`compare-lead-${label}`}><TrendingUp className="w-2.5 h-2.5"/> Leads on reach</span>}
      </div>
      <div className="aspect-square bg-black/50 rounded-2xl overflow-hidden border border-white/10 flex items-center justify-center">
        {creative.type === 'video' && creative.video_url
          ? <video src={creative.video_url} controls loop muted playsInline className="w-full h-full object-cover"/>
          : <img src={creative.image_url} alt={creative.prompt} className="w-full h-full object-cover"/>}
      </div>
      <div className="text-xs text-white/55 line-clamp-2 mt-3">{creative.prompt}</div>
      {perf && (
        <div className="mt-3 glass rounded-xl px-3 py-2.5" data-testid={`compare-perf-${label}`}>
          {noData ? (
            <div className="text-[11px] text-white/40 text-center py-1">Not published yet — no live data</div>
          ) : (
            <div className="flex gap-2 text-center">
              <Metric label="Reach" value={perf.reach?.toLocaleString()} lead={lead}/>
              <Metric label="Impr." value={perf.impressions?.toLocaleString()}/>
              <Metric label="Engage" value={perf.engagement?.toLocaleString()}/>
            </div>
          )}
        </div>
      )}
      <button onClick={()=>onPick(creative)} data-testid={`compare-pick-${label}`}
        className="mt-4 btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2">
        <Trophy className="w-4 h-4"/> Pick as winner
      </button>
    </div>
  );
}

export function CompareDialog({ a, b, onClose, onPickWinner }) {
  const [perfA, setPerfA] = useState(null);
  const [perfB, setPerfB] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [ra, rb] = await Promise.all([
          api.get(`/creatives/${a.creative_id}/performance`),
          api.get(`/creatives/${b.creative_id}/performance`),
        ]);
        setPerfA(ra.data); setPerfB(rb.data);
      } catch (e) { console.error('Performance load failed', e); }
    })();
  }, [a.creative_id, b.creative_id]);

  const aReach = perfA?.has_data ? perfA.reach : -1;
  const bReach = perfB?.has_data ? perfB.reach : -1;
  const leadA = perfA?.has_data && aReach > bReach;
  const leadB = perfB?.has_data && bReach > aReach;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="compare-dialog">
      <div className="glass rounded-3xl w-full max-w-3xl p-6 md:p-8 relative border border-white/10">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="compare-close"><X className="w-5 h-5"/></button>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-yellow mb-1">A/B Compare</div>
        <h2 className="font-display font-black text-2xl tracking-tighter text-3d">Which creative performs?</h2>
        <p className="text-white/55 text-sm mt-1 mb-6">Review both side by side — live reach where published — then pick the winner to publish.</p>
        <div className="flex flex-col md:flex-row gap-6">
          <Pane creative={a} label="A" perf={perfA} lead={leadA} onPick={onPickWinner}/>
          <div className="hidden md:flex items-center">
            <span className="font-display font-black text-xl text-white/25">VS</span>
          </div>
          <Pane creative={b} label="B" perf={perfB} lead={leadB} onPick={onPickWinner}/>
        </div>
        <div className="mt-6 text-center text-[11px] text-white/40 inline-flex items-center gap-1.5 w-full justify-center">
          <Send className="w-3 h-3"/> Picking a winner marks it and opens the publisher for that creative.
        </div>
      </div>
    </div>
  );
}
